/**
 * useMapGpu - does this screen draw the map's world on the GPU? (perf overhaul, Stage 2b)
 *
 * EVERY BROWSER'S DEFAULT since 2026-10-30 (the user's word, after the table's laptop played it smooth): the canvas
 * draws the map's world unless this browser opted out, `localStorage['dw-map-gpu'] = '0'` - then the svg draws
 * everything as it did before (the perf harness's `--svg`, the E2E suite's `DW_E2E_MAP_SVG=1`). Read once per mount:
 * flipping it takes a reload, as a renderer change should.
 *
 * `drawing` turns true only once the canvas has drawn its first frame (`ready`): until then - the three.js chunk
 * still loading - the svg keeps drawing, so the map is never blank. A browser that cannot start WebGL falls back to
 * the svg for good on this mount: the canvas calls `unavailable` with the reason, logged once (a map that silently
 * lost its GPU path would look like a performance bug).
 *
 * @returns {{ on: boolean, drawing: boolean, ready: () => void, unavailable: (reason: unknown) => void }}
 */
import { useCallback, useState } from 'react';

const KEY = 'dw-map-gpu';

/** The viewer's switch: on unless opted out; a storage that throws (a private window, blocked site data) is on. */
function mapGpuSwitch() {
  try {
    return window.localStorage.getItem(KEY) !== '0';
  } catch {
    return true;
  }
}

export default function useMapGpu() {
  const [on, setOn] = useState(mapGpuSwitch);
  const [isReady, setReady] = useState(false);
  const ready = useCallback(() => setReady(true), []);
  const unavailable = useCallback((reason) => {
    console.error('The map could not start WebGL; drawing it with SVG instead.', reason);
    setOn(false);
  }, []);
  return { on, drawing: on && isReady, ready, unavailable };
}
